import { useState, useMemo, useCallback } from "react";
import Tabs from "../tabs";
import { Card, Typography, Stack, Box, Chip, CardContent, IconButton, MenuItem, Menu, ListItemIcon, ListItemText } from "@mui/material";
import AppointmentModal from "./appointmentModal";
import TodayIcon from "@mui/icons-material/Today";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CancelIcon from "@mui/icons-material/Cancel";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EventIcon from "@mui/icons-material/Event";
import ScheduleIcon from "@mui/icons-material/Schedule";
import UpdateIcon from "@mui/icons-material/Update";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";

import MultiSelectWithAll from "../../../../components/selections/multiSelectWithAll";
import DataTableWithSearch from "../../../../components/dataTableWithSearch/DataTableWithSearch";
import SnackbarMessage from "../../../../components/snackBar/snackBar";
import { APP_ROUTES } from "../../../../config/appRoutes";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";


const DEPARTMENTS = [
  { id: "102", name: "Administrative" },
  { id: "135", name: "CallCenter" },
  { id: "148", name: "Finance" },
  { id: "162", name: "Operations" },
  { id: "177", name: "Support" },
];

const TAGS = [
  { id: "372", name: "Event ET X" },
  { id: "365", name: "RIZEE" },
  { id: "389", name: "Training" },
  { id: "401", name: "Workshop" },
  { id: "415", name: "Webinar" },
];


const TABLE_DATA = Array.from({ length: 10 }, (_, index) => {
  const dept = DEPARTMENTS[index % DEPARTMENTS.length];
  const tag = TAGS[index % TAGS.length];

  return {
    id: index + 1,
    name: `Patient ${index + 1}`,
    appointment_date: "20-12-2025",
    next_visit_date: index % 4 === 0 ? "22-12-2025" : "-",

    slot_id: index + 1,
    slot_name: ["10 AM - 12 PM", "1 PM - 3 PM", "3 PM - 5 PM"][index % 3],

    appointment_for: ["RAMESH", "NAIDU", "RAMANNA"][index % 3],
    designation: ["GENERAL PHYSICIAN", "DIETITIAN"][index % 2],

    branch_department_id: dept.id,
    branch_department_name: dept.name,

    tag_id: tag.id,
    tag_name: tag.name,

    status_id: (index % 4) + 1,
    status_name: ["Completed", "Waiting", "Scheduled", "Cancelled"][index % 4],
  };
});

console.log("TABLE_DATA", TABLE_DATA);


const CARD_DATA = [
  { id: 1, label: "Today Appointments", count: 4, icon: TodayIcon, color: "#1976d2" },
  { id: 2, label: "Completed", count: 3, icon: CheckCircleIcon, color: "#2e7d32" },
  { id: 3, label: "Waiting", count: 3, icon: HourglassEmptyIcon, color: "#ed6c02" },
  { id: 4, label: "Scheduled", count: 4, icon: EventAvailableIcon, color: "#8B5CF6" },
  { id: 5, label: "Cancelled", count: 2, icon: CancelIcon, color: "#d32f2f" },
];

function AppointmentList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [state, setState] = useState({
    activeCard: 1,
    selectedBranchDept: [],
    selectedEmployee: [],
    openModal: false,
    selectedAction: null,
    selectedRowData: [],
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const { activeCard, openModal, selectedAction, selectedRowData } = state;

  const handleChangeCard = (id) => {
    setState(prev => ({ ...prev, activeCard: id }));
  };

  const columns = useMemo(() => [
    { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
    {
      accessorKey: "name",
      header: "Name",
      Cell: ({ row }) => (
        <span
          style={{ display: "inline-block" }}
        >
          <Box
            onClick={() => navigate(APP_ROUTES.APPOINTMENTS.PATIENT_HISTORY)}
            component="span"
            sx={{
              cursor: "pointer",
              color: `var(--link)`,
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {row.original.name}
          </Box>
        </span>
      )
    },
    { header: "Appointment Date", accessorKey: "appointment_date" },
    { header: "Slot Name", accessorKey: "slot_name" },
    { header: "Appointment For", accessorKey: "appointment_for" },
    { header: "Designation", accessorKey: "designation" },
    { header: "Branch & Departments", accessorKey: "branch_department_name" },
    {
      header: "Status", accessorKey: "status_name", Cell: ({ row }) => {
        const status = row.original.status_name || "";

        const colorMap = {
          completed: "var(--success)",
          waiting: "var(--btn-7)",
          scheduled: "var(--btn-8)",
          cancelled: "var(--error)",
        };

        const bgColor = colorMap[status.toLowerCase()] || "var(--btn-13)";

        return (
          <Chip
            label={status.replaceAll("_", " ")}
            size="small"
            sx={{
              backgroundColor: bgColor,
              color: "#fff",
              textTransform: "capitalize",
              fontWeight: 500,
            }}
          />
        );
      },
    },
    { header: "Next Visit Date", accessorKey: "next_visit_date" },
    {
      header: "Action",
      accessorKey: "actions",
      Cell: ({ row }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);

        const handleOpen = useCallback((event) => {
          setAnchorEl(event.currentTarget);
        }, []);

        const handleClose = useCallback(() => {
          setAnchorEl(null);
        }, []);


        const handleActionClick = (action) => {
          setState(prev => ({ ...prev, selectedAction: action, openModal: true, selectedRowData: [row.original] }));
          setAnchorEl(null); // close menu
        };


        const actionList = [
          {
            key: "nextVisit",
            label: "Next Visit Date",
            icon: <EventIcon fontSize="small" color="primary" />,
          },
          {
            key: "reschedule",
            label: "Re-schedule Slot",
            icon: <ScheduleIcon fontSize="small" color="warning" />,
          },
          ...row.original.status_id === 1 ? [] : [{
            key: "status",
            label: "Update Status",
            icon: <UpdateIcon fontSize="small" color="info" />,
          }],
          {
            key: "prescription",
            label: "Prescriptions",
            icon: <MedicalInformationIcon fontSize="small" color="success" />,
          },
        ];

        return (
          <>
            <IconButton
              size="small"
              aria-label="more actions"
              aria-controls={open ? "action-menu" : undefined}
              aria-expanded={open ? "true" : undefined}
              aria-haspopup="true"
              onClick={handleOpen}
              disabled={row.original.status_id === 4}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>

            <Menu
              id="action-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    width: 220,
                    borderRadius: 2,
                  },
                },
              }}
            >
              {actionList.map((item, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleActionClick(item.key)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </MenuItem>
              ))}
            </Menu>
          </>
        );
      },
    }
  ], [navigate]);

  return (
    <div>
      <Tabs />

      {/* Cards Row */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        mt={2}
      >
        {CARD_DATA.map(({ id, label, count, icon: Icon, color }) => {
          const isActive = activeCard === id;

          return (
            <Card
              key={id}
              onClick={() => handleChangeCard(id)}
              sx={{
                flex: 1,
                height: 85,
                px: 1.5,
                cursor: "pointer",
                borderRadius: 2,
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transition: "0.25s ease",
                border: `1.5px solid ${isActive ? color : "transparent"}`,
                boxShadow: isActive
                  ? `0 6px 14px ${color}55`
                  : "0 2px 8px rgba(0,0,0,0.08)",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: `0 10px 18px ${color}66`,
                },
              }}
            >
              {/* Gradient Top Strip */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: 4,
                  width: "100%",
                  background: `linear-gradient(90deg, ${color}, transparent)`,
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                }}
              />

              {/* Left Content */}
              <Stack spacing={0.3}>
                <Typography
                  variant="body"
                  color={isActive ? `${color}` : "var(--dark-bg)"}
                  lineHeight={2}
                >
                  {label}
                </Typography>

                <Typography variant="h5" fontWeight={700}>
                  {count}
                </Typography>
              </Stack>

              {/* Icon */}
              <Box
                sx={{
                  backgroundColor: color,
                  color: "#fff",
                  borderRadius: "50%",
                  p: 1.5,
                  display: "flex",
                  transition: "all 0.35s ease",
                }}
              >
                <Icon fontSize="small" />
              </Box>
            </Card>
          );
        })}
      </Stack>


      <Box mt={2}>
        <Card>
          <CardContent>
            <DataTableWithSearch
              columns={columns}
              data={TABLE_DATA}
              // renderFilters={renderAppointmentFilter()}
              status={false}
            />
          </CardContent>
        </Card>
      </Box>

      <SnackbarMessage open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} />

      <AppointmentModal
        show={openModal}
        handleClose={() => setState(prev => ({ ...prev, openModal: false, selectedRowData: [] }))}
        selectedAction={selectedAction}
        rowData={selectedRowData}
      />
    </div>
  );
}

export default AppointmentList;